import React, { useRef, useEffect } from 'react';
import { useCompoundBody } from '@react-three/cannon';
import { Text } from '@react-three/drei';
import * as THREE from 'three';

// --- INSTANCED MESH COMPONENTS FOR MASSIVE MICRO-DETAILING ---

// --- CLASSICAL ARCHITECTURAL DETAILS ---

const WindowGrilles = ({ numPillars, pillarSpacing, width, zPos }) => {
  const numWindows = numPillars - 1;
  const barsPerWindow = 12; 
  const count = numWindows * barsPerWindow;
  const meshRef = useRef();

  const geometry = React.useMemo(() => new THREE.BoxGeometry(1, 1, 1), []);
  const material = React.useMemo(() => new THREE.MeshStandardMaterial({
    color: "#222",
    metalness: 0.9,
    roughness: 0.3
  }), []);

  useEffect(() => {
    if (!meshRef.current) return;
    const dummy = new THREE.Object3D();
    let i = 0;
    for (let w = 0; w < numWindows; w++) {
      const xBase = -width / 2 + pillarSpacing / 2 + w * pillarSpacing;
      for (let v = 0; v < 4; v++) {
        dummy.position.set(xBase - 0.6 + v * 0.4, 11, zPos);
        dummy.scale.set(0.04, 3.8, 0.04);
        dummy.updateMatrix();
        meshRef.current.setMatrixAt(i++, dummy.matrix);
      }
      for (let h = 0; h < 8; h++) {
        dummy.position.set(xBase, 9.4 + h * 0.45, zPos);
        dummy.scale.set(1.8, 0.04, 0.04);
        dummy.updateMatrix();
        meshRef.current.setMatrixAt(i++, dummy.matrix);
      }
    }
    meshRef.current.instanceMatrix.needsUpdate = true;
  }, [numPillars, pillarSpacing, width, zPos]);

  return (
    <instancedMesh ref={meshRef} args={[geometry, material, count]} castShadow />
  );
};

const Corbels = ({ numPillars, pillarSpacing, width, yPos, zPos, textures }) => {
  const numWindows = numPillars - 1;
  const count = numWindows * 4; 
  const meshRef = useRef();

  const geometry = React.useMemo(() => {
    const geo = new THREE.CylinderGeometry(0.2, 0.1, 0.6, 4);
    // Rotate 45deg to create bracket wedge shape from a box
    geo.rotateX(Math.PI/4);
    return geo;
  }, []);
  const material = React.useMemo(() => new THREE.MeshStandardMaterial({
    map: textures?.diff || null,
    roughnessMap: textures?.rough || null,
    color: "#EAE0CC"
  }), [textures]);

  useEffect(() => {
    if (!meshRef.current) return;
    const dummy = new THREE.Object3D();
    let idx = 0;
    for(let w = 0; w < numWindows; w++) {
       const xBase = -width / 2 + pillarSpacing / 2 + w * pillarSpacing;
       for(let b = 0; b < 4; b++) {
          dummy.position.set(xBase - 1.2 + b * 0.8, yPos, zPos);
          dummy.updateMatrix();
          meshRef.current.setMatrixAt(idx++, dummy.matrix);
       }
    }
    meshRef.current.instanceMatrix.needsUpdate = true;
  }, [numPillars, pillarSpacing, width, yPos, zPos]);

  return (
    <instancedMesh ref={meshRef} args={[geometry, material, count]} castShadow />
  );
};

const ACUnits = ({ numPillars, pillarSpacing, width, zPos }) => {
  const count = Math.floor(width / 3); 
  const meshRef = useRef();

  const geometry = React.useMemo(() => new THREE.BoxGeometry(0.8, 0.6, 0.5), []);
  const material = React.useMemo(() => new THREE.MeshStandardMaterial({
    color: "#cccccc",
    metalness: 0.6,
    roughness: 0.5
  }), []);

  useEffect(() => {
    if (!meshRef.current) return;
    const dummy = new THREE.Object3D();
    for (let i = 0; i < count; i++) {
       const w = Math.floor(Math.random() * (numPillars - 1));
       const xBase = -width / 2 + pillarSpacing / 2 + w * pillarSpacing;
       const side = Math.random() > 0.5 ? 1 : -1;
       const x = xBase + side * 1.5;
       const y = 8 + Math.random() * 6;
       dummy.position.set(x, y, zPos + 0.3);
       dummy.rotation.set(0, (Math.random() - 0.5) * 0.1, 0);
       dummy.updateMatrix();
       meshRef.current.setMatrixAt(i, dummy.matrix);
    }
    meshRef.current.instanceMatrix.needsUpdate = true;
  }, [numPillars, pillarSpacing, width, zPos, count]);

  return (
    <instancedMesh ref={meshRef} args={[geometry, material, count]} castShadow />
  );
};

const Dentils = ({ width, yPos, zPos, textures }) => {
  const count = Math.floor(width / 0.4); 
  const meshRef = useRef();
  
  const geometry = React.useMemo(() => new THREE.BoxGeometry(0.2, 0.3, 0.2), []);
  const material = React.useMemo(() => new THREE.MeshStandardMaterial({
    map: textures?.diff || null,
    roughnessMap: textures?.rough || null,
    color: "#EAE0CC"
  }), [textures]);

  useEffect(() => {
    if (!meshRef.current) return;
    const dummy = new THREE.Object3D();
    const spacing = width / count;
    for(let i = 0; i < count; i++) {
       dummy.position.set(-width/2 + i * spacing + spacing/2, 0, 0);
       dummy.updateMatrix();
       meshRef.current.setMatrixAt(i, dummy.matrix);
    }
    meshRef.current.instanceMatrix.needsUpdate = true;
  }, [width, count]);
  
  return (
    <instancedMesh ref={meshRef} args={[geometry, material, count]} position={[0, yPos, zPos]} castShadow />
  );
};

const Balusters = ({ width, yPos, zPos, textures }) => {
  const count = Math.floor(width / 0.8); 
  const meshRef = useRef();
  
  const geometry = React.useMemo(() => new THREE.CylinderGeometry(0.15, 0.2, 1.2, 8), []);
  const material = React.useMemo(() => new THREE.MeshStandardMaterial({
    map: textures?.diff || null,
    roughnessMap: textures?.rough || null,
    color: "#EAE0CC"
  }), [textures]);

  useEffect(() => {
    if (!meshRef.current) return;
    const dummy = new THREE.Object3D();
    const spacing = width / count;
    for(let i = 0; i < count; i++) {
       dummy.position.set(-width/2 + i * spacing + spacing/2, 0, 0);
       dummy.updateMatrix();
       meshRef.current.setMatrixAt(i, dummy.matrix);
    }
    meshRef.current.instanceMatrix.needsUpdate = true;
  }, [width, count]);
  
  return (
    <instancedMesh ref={meshRef} args={[geometry, material, count]} position={[0, yPos, zPos]} castShadow />
  );
};

const RusticatedPillarRings = ({ numPillars, pillarSpacing, width, depth, textures }) => {
  const ringsPerPillar = 11;
  const count = numPillars * ringsPerPillar;
  const meshRef = useRef();
  
  const geometry = React.useMemo(() => new THREE.CylinderGeometry(0.72, 0.72, 0.15, 16), []);
  const material = React.useMemo(() => new THREE.MeshStandardMaterial({
    map: textures?.diff || null,
    roughnessMap: textures?.rough || null,
    color: "#F5F0E6"
  }), [textures]);

  useEffect(() => {
    if (!meshRef.current) return;
    const dummy = new THREE.Object3D();
    let idx = 0;
    for(let p = 0; p < numPillars; p++) {
       const xPos = -width / 2 + p * pillarSpacing;
       for(let r = 0; r < ringsPerPillar; r++) {
          const yPos = 0.5 + r * 0.5; 
          dummy.position.set(xPos, yPos, depth / 2 - 1);
          dummy.updateMatrix();
          meshRef.current.setMatrixAt(idx++, dummy.matrix);
       }
    }
    meshRef.current.instanceMatrix.needsUpdate = true;
  }, [numPillars, pillarSpacing, width, depth, count]);
  
  return (
    <instancedMesh ref={meshRef} args={[geometry, material, count]} castShadow receiveShadow />
  );
};

const ArchKeystones = ({ numPillars, pillarSpacing, width, depth, textures }) => {
  const count = numPillars - 1;
  const meshRef = useRef();
  
  const geometry = React.useMemo(() => new THREE.BoxGeometry(0.6, 1.0, 0.4), []);
  const material = React.useMemo(() => new THREE.MeshStandardMaterial({
    map: textures?.diff || null,
    roughnessMap: textures?.rough || null,
    color: "#F5F0E6"
  }), [textures]);

  useEffect(() => {
    if (!meshRef.current) return;
    const dummy = new THREE.Object3D();
    for(let i = 0; i < count; i++) {
       const xPos = -width / 2 + pillarSpacing / 2 + i * pillarSpacing;
       dummy.position.set(xPos, 5.5, depth / 2 - 1 + 0.2);
       dummy.updateMatrix();
       meshRef.current.setMatrixAt(i, dummy.matrix);
    }
    meshRef.current.instanceMatrix.needsUpdate = true;
  }, [numPillars, pillarSpacing, width, depth, count]);

  return (
    <instancedMesh ref={meshRef} args={[geometry, material, count]} castShadow />
  );
};

const CP_BRANDS = [
  { name: "Wenger's", bg: "#4A3B32", color: "#F5F0E6" },
  { name: "Starbucks", bg: "#006241", color: "#FFFFFF" },
  { name: "H&M", bg: "#FFFFFF", color: "#CD0000" },
  { name: "Haldiram's", bg: "#D32F2F", color: "#FFEB3B" },
  { name: "United Coffee House", bg: "#2C3E50", color: "#EAE0CC" },
  { name: "Keventers", bg: "#FF9800", color: "#FFFFFF" },
  { name: "PVR Plaza", bg: "#000000", color: "#FFD700" },
  { name: "Levi's", bg: "#B71C1C", color: "#FFFFFF" },
  { name: "Zara", bg: "#000000", color: "#FFFFFF" },
  { name: "Apple", bg: "#F5F5F7", color: "#333333" },
  { name: "Sony", bg: "#000000", color: "#FFFFFF" },
  { name: "Nike", bg: "#111111", color: "#FFFFFF" },
  { name: "Reebok", bg: "#FFFFFF", color: "#D32F2F" },
  { name: "Adidas", bg: "#000000", color: "#FFFFFF" },
  { name: "Puma", bg: "#D32F2F", color: "#FFFFFF" },
  { name: "Bata", bg: "#D32F2F", color: "#FFFFFF" },
  { name: "FabIndia", bg: "#8D6E63", color: "#FFFFFF" },
  { name: "Biba", bg: "#E91E63", color: "#FFFFFF" },
  { name: "W for Woman", bg: "#9C27B0", color: "#FFFFFF" },
  { name: "Marks & Spencer", bg: "#000000", color: "#FFFFFF" },
  { name: "Allen Solly", bg: "#4CAF50", color: "#FFFFFF" },
  { name: "Van Heusen", bg: "#1A237E", color: "#FFFFFF" },
  { name: "Louis Philippe", bg: "#212121", color: "#FFFFFF" },
  { name: "Peter England", bg: "#FF5722", color: "#FFFFFF" },
  { name: "Raymond", bg: "#3E2723", color: "#FFFFFF" },
  { name: "Woodland", bg: "#33691E", color: "#FFFFFF" },
  { name: "Skechers", bg: "#1565C0", color: "#FFFFFF" },
  { name: "Asics", bg: "#0D47A1", color: "#FFFFFF" },
  { name: "Croma", bg: "#00BCD4", color: "#FFFFFF" },
  { name: "Reliance Digital", bg: "#F44336", color: "#FFFFFF" },
  { name: "The Body Shop", bg: "#388E3C", color: "#FFFFFF" },
  { name: "MAC", bg: "#000000", color: "#FFFFFF" },
  { name: "Forest Essentials", bg: "#5D4037", color: "#FFFFFF" },
  { name: "Nykaa", bg: "#E91E63", color: "#FFFFFF" },
  { name: "Kama Ayurveda", bg: "#4E342E", color: "#F5F0E6" },
  { name: "Khadi India", bg: "#FFFFFF", color: "#1B5E20" },
  { name: "Meena Bazaar", bg: "#C62828", color: "#FFFFFF" },
  { name: "Nalli", bg: "#FFB300", color: "#D84315" },
  { name: "Pind Balluchi", bg: "#D84315", color: "#FFFFFF" },
  { name: "Saravana Bhavan", bg: "#FFFFFF", color: "#00695C" },
  { name: "Farzi Cafe", bg: "#212121", color: "#FFCA28" },
  { name: "Lord of the Drinks", bg: "#111111", color: "#FBC02D" },
  { name: "Chaayos", bg: "#FFC107", color: "#3E2723" },
  { name: "Cafe Coffee Day", bg: "#D32F2F", color: "#FFFFFF" },
  { name: "The Irish House", bg: "#1B5E20", color: "#FFFFFF" },
  { name: "Ministry of Beer", bg: "#212121", color: "#FF9800" },
  { name: "McDonald's", bg: "#D32F2F", color: "#FFC107" },
  { name: "KFC", bg: "#D32F2F", color: "#FFFFFF" }
];

const FacadeSigns = ({ numPillars, pillarSpacing, width, zPos, buildingIndex }) => {
  const count = numPillars - 1; // 6 bays

  return (
    <group>
      {Array.from({ length: count }).map((_, w) => {
        const xBase = -width / 2 + pillarSpacing / 2 + w * pillarSpacing;
        const brandIndex = ((buildingIndex || 0) * count + w) % CP_BRANDS.length;
        const brand = CP_BRANDS[brandIndex];
        return (
          <group key={`fsign-${w}`} position={[xBase, 7.5, zPos]}>
            <mesh>
              <boxGeometry args={[pillarSpacing, 1.8, 0.2]} />
              <meshStandardMaterial color={brand.bg} roughness={0.5} />
            </mesh>
            <Text position={[0, 0, 0.12]} fontSize={0.7} color={brand.color} anchorX="center" anchorY="middle">
              {brand.name}
            </Text>
          </group>
        );
      })}
    </group>
  );
};

const HangingSigns = ({ numPillars, pillarSpacing, width, buildingIndex }) => {
  const count = numPillars - 1;

  return (
    <group>
      {Array.from({ length: count }).map((_, w) => {
        const xBase = -width / 2 + pillarSpacing / 2 + w * pillarSpacing;
        // Offset brand by +2 for HangingSigns so they don't perfectly match Facade
        const brandIndex = ((buildingIndex || 0) * count + w + 2) % CP_BRANDS.length;
        const brand = CP_BRANDS[brandIndex];
        return (
          <group key={`hsign-${w}`} position={[xBase, 4.0, 2.5]}>
            <mesh castShadow>
              <boxGeometry args={[0.2, 1.8, 2.8]} />
              <meshStandardMaterial color={brand.bg} roughness={0.2} emissive={brand.bg} emissiveIntensity={0.3} />
            </mesh>
            <Text position={[0.11, 0, 0]} rotation={[0, Math.PI / 2, 0]} fontSize={0.5} color={brand.color} anchorX="center" anchorY="middle">
              {brand.name}
            </Text>
            <Text position={[-0.11, 0, 0]} rotation={[0, -Math.PI / 2, 0]} fontSize={0.5} color={brand.color} anchorX="center" anchorY="middle">
              {brand.name}
            </Text>
          </group>
        );
      })}
    </group>
  );
};

const PlinthHedges = ({ numPillars, pillarSpacing, width, zPos }) => {
  const count = numPillars - 1;
  const meshRef = useRef();

  const geometry = React.useMemo(() => new THREE.BoxGeometry(pillarSpacing * 0.8, 0.6, 0.8), [pillarSpacing]);
  const material = React.useMemo(() => new THREE.MeshStandardMaterial({
    color: "#2e4a2b", 
    roughness: 0.9,
    metalness: 0.1
  }), []);

  useEffect(() => {
    if (!meshRef.current) return;
    const dummy = new THREE.Object3D();
    for (let w = 0; w < count; w++) {
      const xBase = -width / 2 + pillarSpacing / 2 + w * pillarSpacing;
      dummy.position.set(xBase, 0.8, zPos); 
      dummy.updateMatrix();
      meshRef.current.setMatrixAt(w, dummy.matrix);
    }
    meshRef.current.instanceMatrix.needsUpdate = true;
  }, [numPillars, pillarSpacing, width, zPos, count]);

  return (
    <instancedMesh ref={meshRef} args={[geometry, material, count]} castShadow />
  );
};

const PottedPlants = ({ width, zPos }) => {
  const count = Math.floor(width / 1.5); 
  const meshRef = useRef();
  
  // Create a combined geometry of a pot and a plant bush
  const geometry = React.useMemo(() => {
    const potGeo = new THREE.CylinderGeometry(0.2, 0.15, 0.4, 8);
    potGeo.translate(0, 0.2, 0);
    const bushGeo = new THREE.DodecahedronGeometry(0.3, 1);
    bushGeo.translate(0, 0.6, 0);
    return { potGeo, bushGeo };
  }, []);

  const materialPot = React.useMemo(() => new THREE.MeshStandardMaterial({ color: "#8B4513" }), []);
  const materialBush = React.useMemo(() => new THREE.MeshStandardMaterial({ color: "#228B22", roughness: 0.8 }), []);

  const meshRefPot = useRef();
  const meshRefBush = useRef();

  useEffect(() => {
    if (!meshRefPot.current || !meshRefBush.current) return;
    const dummy = new THREE.Object3D();
    const spacing = width / count;
    for(let i = 0; i < count; i++) {
       dummy.position.set(-width/2 + i * spacing + spacing/2 + (Math.random()-0.5)*0.2, 17.5, zPos + (Math.random()-0.5)*0.2);
       const scale = 0.8 + Math.random() * 0.4;
       dummy.scale.set(scale, scale, scale);
       dummy.rotation.set(0, Math.random() * Math.PI, 0);
       dummy.updateMatrix();
       meshRefPot.current.setMatrixAt(i, dummy.matrix);
       meshRefBush.current.setMatrixAt(i, dummy.matrix);
    }
    meshRefPot.current.instanceMatrix.needsUpdate = true;
    meshRefBush.current.instanceMatrix.needsUpdate = true;
  }, [width, count, zPos]);
  
  return (
    <group>
      <instancedMesh ref={meshRefPot} args={[geometry.potGeo, materialPot, count]} castShadow />
      <instancedMesh ref={meshRefBush} args={[geometry.bushGeo, materialBush, count]} castShadow />
    </group>
  );
};

const RecessedBayWindows = ({ numPillars, pillarSpacing, width, zPos }) => {
  const count = numPillars - 1;
  
  // Geometries for detailed window
  const frameGeo = React.useMemo(() => new THREE.BoxGeometry(2.4, 4.4, 0.4), []);
  const glassGeo = React.useMemo(() => new THREE.BoxGeometry(2.2, 4.2, 0.1), []);
  const paneVGeo = React.useMemo(() => new THREE.BoxGeometry(0.05, 4.2, 0.2), []);
  const paneHGeo = React.useMemo(() => new THREE.BoxGeometry(2.2, 0.05, 0.2), []);

  const frameMat = React.useMemo(() => new THREE.MeshStandardMaterial({ color: "#ffffff", roughness: 0.8 }), []);
  const glassMat = React.useMemo(() => new THREE.MeshStandardMaterial({ color: "#111111", roughness: 0.1, metalness: 0.9 }), []);
  
  const frameRef = useRef();
  const glassRef = useRef();
  
  // Assuming 2 vertical panes and 4 horizontal panes per window
  const numPanesV = 2;
  const numPanesH = 4;
  const paneVRef = useRef();
  const paneHRef = useRef();

  useEffect(() => {
    if (!frameRef.current) return;
    const dummy = new THREE.Object3D();
    
    let windowIdx = 0;
    let paneVIdx = 0;
    let paneHIdx = 0;

    for (let w = 0; w < count; w++) {
      const xBase = -width / 2 + pillarSpacing / 2 + w * pillarSpacing;
      
      // Main Frame and Glass (recessed into the wall)
      dummy.position.set(xBase, 11, zPos - 0.2); 
      dummy.updateMatrix();
      frameRef.current.setMatrixAt(windowIdx, dummy.matrix);
      
      dummy.position.set(xBase, 11, zPos - 0.25);
      dummy.updateMatrix();
      glassRef.current.setMatrixAt(windowIdx, dummy.matrix);

      // Vertical Panes
      for(let v=0; v<numPanesV; v++) {
         const px = xBase - 0.6 + v * 1.2;
         dummy.position.set(px, 11, zPos - 0.22);
         dummy.updateMatrix();
         paneVRef.current.setMatrixAt(paneVIdx++, dummy.matrix);
      }

      // Horizontal Panes
      for(let h=0; h<numPanesH; h++) {
         const py = 11 - 1.5 + h * 1.0;
         dummy.position.set(xBase, py, zPos - 0.22);
         dummy.updateMatrix();
         paneHRef.current.setMatrixAt(paneHIdx++, dummy.matrix);
      }
      
      windowIdx++;
    }
    frameRef.current.instanceMatrix.needsUpdate = true;
    glassRef.current.instanceMatrix.needsUpdate = true;
    paneVRef.current.instanceMatrix.needsUpdate = true;
    paneHRef.current.instanceMatrix.needsUpdate = true;
  }, [numPillars, pillarSpacing, width, zPos, count]);

  return (
    <group>
      <instancedMesh ref={frameRef} args={[frameGeo, frameMat, count]} castShadow />
      <instancedMesh ref={glassRef} args={[glassGeo, glassMat, count]} />
      <instancedMesh ref={paneVRef} args={[paneVGeo, frameMat, count * numPanesV]} castShadow />
      <instancedMesh ref={paneHRef} args={[paneHGeo, frameMat, count * numPanesH]} castShadow />
    </group>
  );
};

// --- MAIN GEORGIAN BUILDING (Connaught Place Style) ---

export const ConnaughtBuilding = ({ buildingIndex, position, rotation = [0, 0, 0], width = 36, depth = 15, textures }) => {
  const height = 18; 
  const numPillars = Math.max(3, Math.floor(width / 4)) + 1;
  const pillarSpacing = width / (numPillars - 1);
  const arcadeDepth = 6;
  const facadeZ = depth / 2 - arcadeDepth; // The recessed front wall Z-position

  const shapes = [];

  // 1. Plinth / Sidewalk Collider (Raised platform)
  shapes.push({
    type: 'Box',
    position: [0, 0.25, 0],
    args: [width, 0.5, depth]
  });

  // 2. Rear Shop Wall Collider (Behind arcade)
  shapes.push({
    type: 'Box',
    position: [0, 3.25, -(depth / 2) + ((depth - arcadeDepth) / 2)],
    args: [width, 5.5, depth - arcadeDepth]
  });

  // 3. First Floor & Roof Collider (Overhanging the arcade)
  shapes.push({
    type: 'Box',
    position: [0, height / 2 + 3, 0], 
    args: [width, height - 6, depth] 
  });

  // 4. Individual Pillar Colliders
  for (let i = 0; i < numPillars; i++) {
    const xPos = -width / 2 + i * pillarSpacing;
    shapes.push({
      type: 'Cylinder',
      position: [xPos, 3.5, depth / 2 - 1],
      args: [0.9, 0.9, 6, 16] // topRadius, bottomRadius, height, segments
    });
  }

  const [ref] = useCompoundBody(() => ({
    mass: 0,
    type: 'Static',
    position,
    rotation,
    shapes
  }));

  const wallMaterialProps = textures ? { map: textures.diff, roughnessMap: textures.rough, color: "#F5F0E6" } : { color: "#F5F0E6", roughness: 0.9 };

  return (
    <group ref={ref}>
      {/* Raised Pedestrian Sidewalk / Plinth */}
      <mesh position={[0, 0.25, 0]} castShadow receiveShadow>
        <boxGeometry args={[width, 0.5, depth]} />
        <meshStandardMaterial color="#666666" roughness={1.0} />
      </mesh>

      {/* CORE FLAT WALLS (Backing geometry to block light/holes) */}
      <mesh position={[0, height / 2 + 3, -arcadeDepth / 2 - 0.2]} castShadow receiveShadow>
        <boxGeometry args={[width, height - 6, depth - arcadeDepth]} />
        <meshStandardMaterial {...wallMaterialProps} />
      </mesh>
      <mesh position={[0, 3, -(depth / 2) + ((depth - arcadeDepth) / 2)]} castShadow receiveShadow>
        <boxGeometry args={[width, 6, depth - arcadeDepth]} />
        <meshStandardMaterial {...wallMaterialProps} color="#dcdcdc" />
      </mesh>
      
      {/* 1. SMOOTH STUCCO FACADE (Replaces blocky masonry for realistic Georgian look) */}
      <mesh position={[0, height / 2 + 3, facadeZ]} castShadow receiveShadow>
        <boxGeometry args={[width, height - 6, 0.4]} />
        <meshStandardMaterial {...wallMaterialProps} color="#F5F0E6" />
      </mesh>

      {/* Shop Shutters & Glowing Showroom on ground floor */}
      {Array.from({ length: numPillars - 1 }).map((_, i) => {
        const xPos = -width / 2 + pillarSpacing / 2 + i * pillarSpacing;
        const zBack = -(depth / 2) + ((depth - arcadeDepth) / 2) + 0.1;
        
        // Make the middle bay a Glowing Showroom display case
        const isShowroom = i === Math.floor((numPillars - 1) / 2);

        if (isShowroom) {
          return (
            <group key={`showroom-${i}`} position={[xPos, 2.5, zBack + 1]}>
              {/* Floor of the display case */}
              <mesh position={[0, -2.4, 0]} receiveShadow>
                <boxGeometry args={[pillarSpacing * 0.8, 0.2, 2]} />
                <meshStandardMaterial color="#f5eedc" roughness={0.2} metalness={0.1} />
              </mesh>
              {/* Back glowing wall of the display */}
              <mesh position={[0, 0, -0.9]}>
                <boxGeometry args={[pillarSpacing * 0.8, 5, 0.2]} />
                <meshStandardMaterial color="#ffffff" emissive="#ffddaa" emissiveIntensity={1.5} />
              </mesh>
              {/* Glass Front */}
              <mesh position={[0, 0, 1]}>
                <boxGeometry args={[pillarSpacing * 0.8, 5, 0.1]} />
                <meshStandardMaterial color="#e0f7fa" transparent opacity={0.3} roughness={0.1} />
              </mesh>
              {/* Mannequin / Display Item 1 */}
              <mesh position={[-0.8, -1.5, 0]} castShadow>
                <cylinderGeometry args={[0.2, 0.2, 1.8, 16]} />
                <meshStandardMaterial color="#222" roughness={0.4} emissive="#222" emissiveIntensity={0.2} />
              </mesh>
              {/* Mannequin / Display Item 2 */}
              <mesh position={[0.8, -1.2, 0]} castShadow>
                <boxGeometry args={[0.6, 2.4, 0.4]} />
                <meshStandardMaterial color="#e91e63" roughness={0.6} emissive="#e91e63" emissiveIntensity={0.2} />
              </mesh>
              {/* Interior Point Light moved forward to bleed into arcade */}
              <pointLight position={[0, 1.5, 0.5]} color="#ffeedd" intensity={20} distance={15} />
            </group>
          );
        }

        return (
          <mesh key={`shutter-${i}`} position={[xPos, 2.5, zBack]}>
            <boxGeometry args={[pillarSpacing * 0.7, 5, 0.1]} />
            <meshStandardMaterial color="#4a4a4a" roughness={0.7} metalness={0.5} />
          </mesh>
        );
      })}

      {/* The Colonnaded Arcade Roof */}
      <mesh position={[0, 6, 0]} castShadow receiveShadow>
        <boxGeometry args={[width, 1, depth]} />
        <meshStandardMaterial {...wallMaterialProps} />
      </mesh>

      {/* Arcade Cross Beams */}
      {Array.from({ length: numPillars }).map((_, i) => {
        const xPos = -width / 2 + i * pillarSpacing;
        return (
          <mesh key={`beam-${i}`} position={[xPos, 5.8, -0.5]} castShadow receiveShadow>
            <boxGeometry args={[0.6, 0.4, depth - 2]} />
            <meshStandardMaterial {...wallMaterialProps} />
          </mesh>
        );
      })}
      
      {/* Horizontal Cornices */}
      <mesh position={[0, 6.5, depth / 2]} castShadow>
        <boxGeometry args={[width + 0.5, 0.5, 0.5]} />
        <meshStandardMaterial {...wallMaterialProps} />
      </mesh>
      <mesh position={[0, 15.5, facadeZ]} castShadow>
        <boxGeometry args={[width + 0.5, 0.5, 0.5]} />
        <meshStandardMaterial {...wallMaterialProps} />
      </mesh>

      {/* HIGH-PERFORMANCE INSTANCED DETAILS */}
      <Dentils width={width} yPos={6.2} zPos={depth / 2 + 0.25} textures={textures} />
      <Dentils width={width} yPos={15.2} zPos={facadeZ + 0.25} textures={textures} />
      <Balusters width={width} yPos={16.6} zPos={facadeZ + 0.1} textures={textures} />
      <RusticatedPillarRings numPillars={numPillars} pillarSpacing={pillarSpacing} width={width} depth={depth} textures={textures} />
      <ArchKeystones numPillars={numPillars} pillarSpacing={pillarSpacing} width={width} depth={depth} textures={textures} />
      
      {/* NEW MICRO-DETAILS (Grilles, Corbels, AC Units, Signs) */}
      <WindowGrilles numPillars={numPillars} pillarSpacing={pillarSpacing} width={width} zPos={facadeZ + 0.25} />
      <Corbels numPillars={numPillars} pillarSpacing={pillarSpacing} width={width} yPos={8.7} zPos={facadeZ + 0.2} textures={textures} />
      <ACUnits numPillars={numPillars} pillarSpacing={pillarSpacing} width={width} zPos={facadeZ + 0.2} />
      
      {/* ULTRA-BOOST CONNAUGHT PLACE FEATURES */}
      <FacadeSigns numPillars={numPillars} pillarSpacing={pillarSpacing} width={width} zPos={depth / 2 + 0.55} buildingIndex={buildingIndex} />
      <HangingSigns numPillars={numPillars} pillarSpacing={pillarSpacing} width={width} buildingIndex={buildingIndex} />
      <PlinthHedges numPillars={numPillars} pillarSpacing={pillarSpacing} width={width} zPos={depth / 2 + 0.4} />
      <PottedPlants width={width} zPos={facadeZ} />
      <RecessedBayWindows numPillars={numPillars} pillarSpacing={pillarSpacing} width={width} zPos={facadeZ} />

      {/* Massive Cylindrical Pillars & Classical Arches */}
      {Array.from({ length: numPillars }).map((_, i) => {
        const xPos = -width / 2 + i * pillarSpacing;
        return (
          <group key={`pillar-${i}`} position={[xPos, 3, depth / 2 - 1]}>
            {/* The soaring fluted shaft */}
            <mesh castShadow receiveShadow>
              <cylinderGeometry args={[0.7, 0.9, 6, 16]} />
              <meshStandardMaterial {...wallMaterialProps} color="#F5F0E6" roughness={0.7} />
            </mesh>
            {/* Massive Pedestal Base */}
            <mesh position={[0, -2.7, 0]} castShadow receiveShadow>
              <boxGeometry args={[2.2, 0.8, 2.2]} />
              <meshStandardMaterial {...wallMaterialProps} color="#EAE0CC" />
            </mesh>
            {/* Molded Ring Base */}
            <mesh position={[0, -2.2, 0]} castShadow receiveShadow>
              <cylinderGeometry args={[1.1, 1.3, 0.4, 16]} />
              <meshStandardMaterial {...wallMaterialProps} color="#EAE0CC" />
            </mesh>
            {/* Detailed Classical Capital */}
            <mesh position={[0, 3, 0]} castShadow receiveShadow>
              <boxGeometry args={[2, 0.4, 2]} />
              <meshStandardMaterial {...wallMaterialProps} color="#EAE0CC" />
            </mesh>
            <mesh position={[0, 2.7, 0]} castShadow receiveShadow>
              <cylinderGeometry args={[1.0, 0.7, 0.4, 16]} />
              <meshStandardMaterial {...wallMaterialProps} color="#EAE0CC" />
            </mesh>
            {/* Grand Arches connecting the pillars */}
            {i < numPillars - 1 && (
              <mesh position={[pillarSpacing / 2, 2.6, 0]} castShadow>
                <cylinderGeometry args={[pillarSpacing / 2 - 0.4, pillarSpacing / 2 - 0.4, 1.2, 16, 1, false, 0, Math.PI]} rotation={[Math.PI / 2, 0, 0]} />
                <meshStandardMaterial {...wallMaterialProps} color="#F5F0E6" />
              </mesh>
            )}
          </group>
        );
      })}

      {/* Roof Parapet Wall (Balustrade Wall) */}
      <mesh position={[0, height + 0.5, facadeZ]} castShadow>
        <boxGeometry args={[width, 1, 0.5]} />
        <meshStandardMaterial {...wallMaterialProps} color="#f0f0f0" />
      </mesh>
    </group>
  );
};
